import { useId } from 'react';
import '../training.css';

const WIDTH = 480;
const HEIGHT = 220;
const PADDING_LEFT = 32;
const PADDING_RIGHT = 12;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 28;
const SERIES_CLASSES = ['gym-chart__series--1', 'gym-chart__series--2', 'gym-chart__series--3'];

/**
 * Dependency-free responsive line chart (spec "Progreso de un ejercicio"):
 * one polyline per series (e.g. peso máximo vs. 1RM estimado), brand colors
 * from `training.css`, and the same `<svg role="img">` + sr-only `<table>`
 * pattern as `BarChart` so every number is available to assistive tech and
 * to anyone who wants the raw values, not just the picture.
 */
export function LineChart({ series = [], title, unit }) {
  const titleId = useId();
  const chartWidth = WIDTH - PADDING_LEFT - PADDING_RIGHT;
  const chartHeight = HEIGHT - PADDING_TOP - PADDING_BOTTOM;
  const allPoints = series.flatMap((s) => s.points || []);
  const xs = allPoints.map((p) => p.x);
  const ys = allPoints.map((p) => p.y);
  const minX = xs.length ? Math.min(...xs) : 0;
  const maxX = xs.length ? Math.max(...xs) : 1;
  const maxY = Math.max(1, ...ys);

  function scaleX(x) {
    if (maxX === minX) return PADDING_LEFT + chartWidth / 2;
    return PADDING_LEFT + ((x - minX) / (maxX - minX)) * chartWidth;
  }
  function scaleY(y) {
    const ratio = maxY === 0 ? 0 : y / maxY;
    return PADDING_TOP + chartHeight * (1 - ratio);
  }

  const baselineY = PADDING_TOP + chartHeight;
  const axisLabels = (series[0] && series[0].points) || [];

  return (
    <figure className="gym-chart">
      <figcaption id={titleId} className="gym-chart__title">{title}</figcaption>
      <svg
        className="gym-chart__svg"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        role="img"
        aria-labelledby={titleId}
      >
        <title>{title}</title>
        <line x1={PADDING_LEFT} y1={baselineY} x2={WIDTH - PADDING_RIGHT} y2={baselineY} className="gym-chart__axis" />
        <line x1={PADDING_LEFT} y1={PADDING_TOP} x2={PADDING_LEFT} y2={baselineY} className="gym-chart__axis" />
        {series.map((s, sIndex) => {
          const points = s.points || [];
          const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${scaleX(p.x)},${scaleY(p.y)}`).join(' ');
          const seriesClass = SERIES_CLASSES[sIndex % SERIES_CLASSES.length];
          return (
            <g key={s.name || sIndex} className={seriesClass}>
              <path d={pathD} className="gym-chart__line" fill="none" />
              {points.map((p, i) => (
                <circle key={`${s.name || sIndex}-${i}`} cx={scaleX(p.x)} cy={scaleY(p.y)} r="3.5" className="gym-chart__point" />
              ))}
            </g>
          );
        })}
        {axisLabels.map((p, i) => (
          <text key={i} x={scaleX(p.x)} y={baselineY + 16} className="gym-chart__label" textAnchor="middle">{p.label}</text>
        ))}
      </svg>
      <table className="gym-sr-only">
        <caption>{title}{unit ? ` (${unit})` : ''}</caption>
        <thead>
          <tr>
            <th scope="col">Serie</th>
            <th scope="col">Punto</th>
            <th scope="col">Valor{unit ? ` (${unit})` : ''}</th>
          </tr>
        </thead>
        <tbody>
          {series.flatMap((s) => (s.points || []).map((p, i) => (
            <tr key={`${s.name}-${i}`}>
              <td>{s.name}</td>
              <td>{p.label}</td>
              <td>{p.y}</td>
            </tr>
          )))}
        </tbody>
      </table>
    </figure>
  );
}
